/** @format */

import { Button } from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { API_ENDPOINTS } from "../../../constants/api";
import { actionJustificativeListRequest } from "../../../redux-modules/actions/actionsJustificatives";
import { actionPresenceTypeListRequest } from "../../../redux-modules/actions/actionsPresenceTypes";
import { actionShiftTypeListRequest } from "../../../redux-modules/actions/actionsShiftTypes";
import { TUser } from "../../../redux-modules/declarations/maps/auth";
import {
  TPermessiPresenze,
  TPersonaGiornata,
  TPersonaPresenze,
  TPresenzeDaGestire,
  TPresenzeGiornata,
  TPresenzeMese,
} from "../../../redux-modules/declarations/maps/presenzeVista";
import { TRequestCreateRequest } from "../../../redux-modules/declarations/maps/requests";
import { selectorJustificativeList } from "../../../redux-modules/selectors/selectorsJustificatives";
import { selectorShiftTypeList } from "../../../redux-modules/selectors/selectorsShiftTypes";
import apiClient from "../../../utils/apiClient";
import { CreatePresenceDrawer } from "../../layout/SideDialogs/AddPresence";
import { RequestCreateDrawer } from "../../layout/SideDialogs/RequestCreate";
import UITransition from "../../ui/Transition";
import { FASCE, TUnitaPresenze, TVista } from "./format";
import { TRisorseAzioni } from "./partials/Azioni";
import { DaGestire, periodoDa, TPeriodo, TTipoPeriodo } from "./partials/DaGestire";
import { Giornata, TPreferenzeGiornata } from "./partials/Giornata";
import { Mese } from "./partials/Mese";
import { PannelloGiornata } from "./partials/PannelloGiornata";
import { Intestazione, Pagina, Schede } from "./styles";
import { azioniPresenze, notificaEsito, TEsitoAzione, useLettura } from "./usePresenze";

const CHIAVE_PREFERENZE = "hr.presenze";

interface TPreferenze extends TPreferenzeGiornata {
  periodo: TTipoPeriodo;
}

const PREFERENZE_INIZIALI: TPreferenze = {
  unita: "ore",
  fascia: "lavoro",
  gruppi: [],
  reparti: [],
  chiusi: [],
  periodo: "sette",
};

/** Preferenze della vista nel browser: unità, fascia, filtri, gruppi chiusi e periodo. */
const leggiPreferenze = (): TPreferenze => {
  try {
    const salvate = JSON.parse(window.localStorage.getItem(CHIAVE_PREFERENZE) || "{}");
    return { ...PREFERENZE_INIZIALI, ...salvate };
  } catch (e) {
    return PREFERENZE_INIZIALI;
  }
};

const salvaPreferenze = (preferenze: TPreferenze): void => {
  try {
    window.localStorage.setItem(CHIAVE_PREFERENZE, JSON.stringify(preferenze));
  } catch (e) {
    // archiviazione non disponibile: le preferenze valgono solo per questa visita
  }
};

const VISTE: TVista[] = ["giornata", "da-gestire", "mese"];
const GIORNO = /^\d{4}-\d{2}-\d{2}$/;
const MESE = /^\d{4}-\d{2}$/;

/** Stato della pagina nell'indirizzo: si può tornare indietro e condividere il link. */
interface TIndirizzo {
  vista: TVista;
  data: string;
  pannello: number | null;
  dipendente: number | null;
  mese: string;
  /** giorno aperto nel pannello della scheda Mese */
  giorno: string | null;
}

const leggiIndirizzo = (search: string): TIndirizzo => {
  const q = new URLSearchParams(search);
  const vista = q.get("vista") as TVista;
  const data = q.get("data") || "";
  const mese = q.get("mese") || "";
  const numero = (v: string | null): number | null => (v && /^\d+$/.test(v) ? Number(v) : null);
  return {
    vista: VISTE.indexOf(vista) >= 0 ? vista : "giornata",
    data: GIORNO.test(data) ? data : moment().format("YYYY-MM-DD"),
    pannello: numero(q.get("pannello")),
    dipendente: numero(q.get("dipendente")),
    mese: MESE.test(mese) ? mese : moment().format("YYYY-MM"),
    giorno: GIORNO.test(q.get("giorno") || "") ? q.get("giorno") : null,
  };
};

const scriviIndirizzo = (i: TIndirizzo): string => {
  const q = new URLSearchParams();
  q.set("vista", i.vista);
  if (i.vista === "giornata") {
    q.set("data", i.data);
    if (i.pannello) q.set("pannello", String(i.pannello));
  }
  if (i.vista === "mese") {
    if (i.dipendente) q.set("dipendente", String(i.dipendente));
    q.set("mese", i.mese);
    if (i.giorno && i.dipendente) q.set("giorno", i.giorno);
  }
  return `?${q.toString()}`;
};

interface TNuovo {
  persona: TPersonaPresenze | null;
  data: string;
  intervallo?: [string, string];
}

const PERMESSI_NESSUNO: TPermessiPresenze = {
  modifica: false,
  crea: false,
  elimina: false,
  cambia_orari: false,
  correggi_presenza: false,
  banca_ore: false,
  straordinario: false,
  forfettizzato: false,
  storico: false,
  in_blocco: false,
  gestisci_richieste: false,
};

/**
 * Fase 5: vista Presenze. Tre schede, una lettura per schermata:
 *  - Giornata: GET /presenze/giornata, con il pannello laterale della persona;
 *  - Da gestire: GET /presenze/da-gestire, anche per il numero sulla scheda;
 *  - Mese di una persona: GET /presenze/mese.
 * Ogni azione usa una chiamata esistente; dopo il salvataggio le schermate si rileggono.
 */
export const ViewPresenze = (): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const indirizzo = useMemo(() => leggiIndirizzo(location.search), [location.search]);
  const { vista, data, pannello, dipendente, mese, giorno: giornoMese } = indirizzo;

  const [preferenze, setPreferenze] = useState<TPreferenze>(leggiPreferenze);
  const [periodo, setPeriodo] = useState<TPeriodo>(() =>
    periodoDa(leggiPreferenze().periodo, moment()),
  );
  const [personaNota, setPersonaNota] = useState<TPersonaPresenze | null>(null);
  const [utenti, setUtenti] = useState<TUser[] | null>(null);
  const [nuovaPresenza, setNuovaPresenza] = useState<TNuovo | null>(null);
  const [nuovaRichiesta, setNuovaRichiesta] = useState<TNuovo | null>(null);

  const tipiTurno = useSelector(selectorShiftTypeList);
  const giustificativi = useSelector(selectorJustificativeList);

  // i pannelli esistenti di nuova presenza e nuova richiesta leggono i tipi dallo store
  useEffect(() => {
    dispatch(actionPresenceTypeListRequest());
    dispatch(actionShiftTypeListRequest());
    dispatch(actionJustificativeListRequest({}));
  }, [dispatch]);

  const oggi = moment().format("YYYY-MM-DD");
  const giornata = useLettura<TPresenzeGiornata>(
    API_ENDPOINTS.PRESENZE_GIORNATA,
    useMemo(() => ({ data }), [data]),
    vista === "giornata",
    data === oggi,
    "Impossibile caricare le presenze del giorno",
  );
  const daGestire = useLettura<TPresenzeDaGestire>(
    API_ENDPOINTS.PRESENZE_DA_GESTIRE,
    useMemo(() => ({ dal: periodo.dal, al: periodo.al }), [periodo.dal, periodo.al]),
    // sempre attiva: il numero sulla scheda resta aggiornato anche dalle altre viste
    true,
    vista === "da-gestire" && periodo.al >= oggi,
    "Impossibile caricare le segnalazioni da gestire",
  );
  const meseLettura = useLettura<TPresenzeMese>(
    API_ENDPOINTS.PRESENZE_MESE,
    useMemo(() => (dipendente ? { user_id: String(dipendente), mese } : null), [dipendente, mese]),
    vista === "mese",
    false,
    "Impossibile caricare il mese della persona",
  );

  // persone per i pannelli esistenti e per la scelta della persona del mese, lette una volta
  const serveUtenti = vista === "mese" || !!nuovaPresenza || !!nuovaRichiesta;
  useEffect(() => {
    if (!serveUtenti || utenti) return;
    apiClient
      .get(API_ENDPOINTS.USERS_LIST + "?all=true")
      .then(res => setUtenti((res.data?.data || []) as TUser[]))
      .catch(() => setUtenti([]));
  }, [serveUtenti, utenti]);

  // ------------------------------------------------------------------ navigazione

  const vai = useCallback(
    (modifica: Partial<TIndirizzo>, aggiungi = false): void => {
      // indirizzo completo (percorso e parametri), costruito dall'indirizzo attuale
      const { pathname, search } = history.location;
      const nuovo = `${pathname}${scriviIndirizzo({ ...leggiIndirizzo(search), ...modifica })}`;
      if (aggiungi) history.push(nuovo);
      else history.replace(nuovo);
    },
    [history],
  );

  const aggiornaPreferenze = useCallback((modifica: Partial<TPreferenze>): void => {
    setPreferenze(attuali => {
      const nuove = { ...attuali, ...modifica };
      salvaPreferenze(nuove);
      return nuove;
    });
  }, []);

  const cambiaPeriodo = useCallback(
    (nuovo: TPeriodo): void => {
      setPeriodo(nuovo);
      if (nuovo.tipo !== "scelto") aggiornaPreferenze({ periodo: nuovo.tipo });
    },
    [aggiornaPreferenze],
  );

  const apriGiornata = useCallback(
    (persona: TPersonaPresenze, giorno: string): void => {
      setPersonaNota(persona);
      vai({ vista: "giornata", data: giorno, pannello: persona.id }, true);
    },
    [vai],
  );

  const apriPersona = useCallback(
    (persona: TPersonaGiornata): void => {
      setPersonaNota(persona);
      vai({ pannello: persona.id }, true);
    },
    [vai],
  );

  const apriMese = useCallback(
    (persona: TPersonaPresenze, giorno: string): void =>
      vai(
        {
          vista: "mese",
          dipendente: persona.id,
          mese: giorno.substr(0, 7),
          pannello: null,
          giorno: null,
        },
        true,
      ),
    [vai],
  );

  // ------------------------------------------------------------------ dopo ogni modifica

  const ricaricaGiornata = giornata.ricarica;
  const ricaricaDaGestire = daGestire.ricarica;
  const ricaricaMese = meseLettura.ricarica;
  const rileggi = useCallback((): void => {
    ricaricaGiornata();
    ricaricaDaGestire();
    ricaricaMese();
  }, [ricaricaGiornata, ricaricaDaGestire, ricaricaMese]);

  const permessi: TPermessiPresenze =
    giornata.dati?.permessi ||
    daGestire.dati?.permessi ||
    meseLettura.dati?.permessi ||
    PERMESSI_NESSUNO;
  const tipoPresenzaOrdinariaId =
    giornata.dati?.tipo_presenza_ordinaria_id ||
    daGestire.dati?.tipo_presenza_ordinaria_id ||
    meseLettura.dati?.tipo_presenza_ordinaria_id ||
    1;

  const apriNuovaRichiesta = useCallback(
    (persona: TPersonaPresenze | null, giorno: string, intervallo?: [string, string]): void =>
      setNuovaRichiesta({ persona, data: giorno, intervallo }),
    [],
  );

  const risorse: TRisorseAzioni = {
    permessi,
    modalita:
      giornata.dati?.modalita_presenze ||
      daGestire.dati?.modalita_presenze ||
      meseLettura.dati?.modalita_presenze ||
      "banca_ore",
    tipiTurno,
    tipoPresenzaOrdinariaId,
    onEsito: (esito: TEsitoAzione): void => {
      if (notificaEsito(esito)) rileggi();
    },
    onGiustificativo: apriNuovaRichiesta,
  };

  const salvaRichiesta = (richiesta: TRequestCreateRequest): void => {
    setNuovaRichiesta(null);
    azioniPresenze.nuovaRichiesta(richiesta).then(esito => {
      if (notificaEsito(esito, "Richiesta non inserita")) rileggi();
    });
  };

  // ------------------------------------------------------------------ pannello

  const personaPannello =
    pannello && giornata.dati && giornata.dati.giornata.data === data
      ? giornata.dati.persone.find(p => p.id === pannello) || null
      : null;
  useEffect(() => {
    if (personaPannello) setPersonaNota(personaPannello);
  }, [personaPannello]);
  const notaPannello = personaNota && personaNota.id === pannello ? personaNota : null;

  // pannello aperto dalla scheda Mese: stessi dati del mese, la schermata dietro resta il mese
  const datiMese = meseLettura.dati;
  const personaMese: TPersonaGiornata | null = useMemo(() => {
    if (!giornoMese || !datiMese || datiMese.persona.id !== dipendente) return null;
    const giornoDelMese = datiMese.giorni.find(g => g.data === giornoMese);
    if (!giornoDelMese) return null;
    const versioni = datiMese.contratto.versioni;
    const versione = versioni.find(v => v.dal <= giornoMese && giornoMese <= v.al) || versioni[0];
    return {
      ...datiMese.persona,
      contratto: {
        ore_settimanali: versione ? versione.ore_settimanali : null,
        giorni_settimanali: versione ? versione.giorni_settimanali : null,
        ore_giornaliere: versione ? versione.ore_giornaliere : null,
        single_badge: versione ? versione.single_badge : false,
        forfettizzato: null,
        tipologia: versione ? versione.tipologia : null,
      },
      giorno: giornoDelMese,
    };
  }, [datiMese, giornoMese, dipendente]);
  const pannelloMese = vista === "mese" && !!giornoMese && !!dipendente;

  const legenda = useMemo(() => {
    const visti: Record<string, string | null> = {};
    (giustificativi.data || []).forEach(g => {
      visti[g.name] = g.color || null;
    });
    // tipi presenti nel giorno ma non (ancora) nello store
    (giornata.dati?.persone || []).forEach(p =>
      p.giorno.giustificativi.forEach(g => {
        if (!(g.nome in visti)) visti[g.nome] = g.colore;
      }),
    );
    return Object.keys(visti).map(nome => ({ nome, colore: visti[nome] }));
  }, [giustificativi.data, giornata.dati]);

  // i pannelli esistenti riapplicano i valori iniziali a ogni nuovo oggetto: vanno memorizzati,
  // altrimenti la rilettura di ogni minuto cancellerebbe quello che si sta scrivendo
  // id della persona con lo stesso tipo delle opzioni delle finestre (la lista di /users):
  // con un tipo diverso la tendina mostrerebbe l'id al posto del nome
  const idUtente = useCallback(
    (persona: TPersonaPresenze): string | null => {
      const trovato = (utenti || []).find(u => String(u.id) === String(persona.id));
      return trovato ? trovato.id : null;
    },
    [utenti],
  );

  const valoriPresenza = useMemo(() => {
    if (!nuovaPresenza || !nuovaPresenza.persona) return null;
    const id = idUtente(nuovaPresenza.persona);
    return id === null
      ? null
      : {
          user_id: id,
          datetime_from: moment(`${nuovaPresenza.data} 09:00`, "YYYY-MM-DD HH:mm"),
        };
  }, [nuovaPresenza, idUtente]);
  const valoriRichiesta = useMemo(() => {
    if (!nuovaRichiesta) return null;
    const intervallo = nuovaRichiesta.intervallo;
    const date: [moment.Moment, moment.Moment] = intervallo
      ? [moment(intervallo[0], "YYYY-MM-DD HH:mm"), moment(intervallo[1], "YYYY-MM-DD HH:mm")]
      : [moment(nuovaRichiesta.data).startOf("day"), moment(nuovaRichiesta.data).endOf("day")];
    const id = nuovaRichiesta.persona ? idUtente(nuovaRichiesta.persona) : null;
    return { user_id: id === null ? undefined : id, date };
  }, [nuovaRichiesta, idUtente]);

  const fascia = FASCE[preferenze.fascia];
  const anomalie = daGestire.dati?.conteggi.anomalie ?? null;

  return (
    <UITransition>
      <Pagina>
        <Intestazione>
          <div>
            <h1>Presenze</h1>
          </div>
          <div className="azioni">
            {permessi.gestisci_richieste && (
              <Button
                onClick={(): void => apriNuovaRichiesta(null, vista === "giornata" ? data : oggi)}
              >
                Nuova richiesta
              </Button>
            )}
            {permessi.crea && (
              <Button
                type="primary"
                onClick={(): void =>
                  setNuovaPresenza({ persona: null, data: vista === "giornata" ? data : oggi })
                }
              >
                Nuova presenza
              </Button>
            )}
          </div>
        </Intestazione>

        <Schede aria-label="Viste delle presenze">
          <button
            type="button"
            aria-current={vista === "giornata" ? "page" : undefined}
            onClick={(): void => vai({ vista: "giornata" }, true)}
          >
            Giornata
          </button>
          <button
            type="button"
            aria-current={vista === "da-gestire" ? "page" : undefined}
            onClick={(): void => vai({ vista: "da-gestire" }, true)}
            title="Anomalie nel periodo scelto in Da gestire"
          >
            Da gestire
            {anomalie !== null && anomalie > 0 && <span className="conteggio">{anomalie}</span>}
          </button>
          <button
            type="button"
            aria-current={vista === "mese" ? "page" : undefined}
            onClick={(): void => vai({ vista: "mese" }, true)}
          >
            Mese di una persona
          </button>
        </Schede>

        {vista === "giornata" && (
          <Giornata
            lettura={giornata}
            data={data}
            preferenze={preferenze}
            giustificativi={legenda}
            onData={(nuova): void => vai({ data: nuova })}
            onPreferenze={aggiornaPreferenze}
            onApriPersona={apriPersona}
          />
        )}

        {vista === "da-gestire" && (
          <DaGestire
            lettura={daGestire}
            periodo={periodo}
            unita={preferenze.unita}
            gruppi={preferenze.gruppi}
            reparti={preferenze.reparti}
            risorse={{ tipiTurno, onGiustificativo: apriNuovaRichiesta }}
            onPeriodo={cambiaPeriodo}
            onPreferenze={aggiornaPreferenze}
            onModificato={rileggi}
            onApriGiornata={apriGiornata}
          />
        )}

        {vista === "mese" && (
          <Mese
            lettura={meseLettura}
            dipendente={dipendente}
            mese={mese}
            utenti={utenti}
            unita={preferenze.unita}
            onDipendente={(id): void => vai({ dipendente: id })}
            onMese={(nuovo): void => vai({ mese: nuovo })}
            onUnita={(unita: TUnitaPresenze): void => aggiornaPreferenze({ unita })}
            onApriGiorno={(_persona, giorno): void => vai({ giorno }, true)}
            giornoAperto={giornoMese}
          />
        )}
      </Pagina>

      <PannelloGiornata
        aperto={(vista === "giornata" && !!pannello) || pannelloMese}
        data={pannelloMese && giornoMese ? giornoMese : data}
        persona={pannelloMese ? personaMese : personaPannello}
        personaNota={pannelloMese ? datiMese?.persona || null : notaPannello}
        adesso={
          (pannelloMese ? datiMese?.mese.adesso : giornata.dati?.giornata.adesso) ||
          moment().format("YYYY-MM-DD HH:mm")
        }
        modalita={
          (pannelloMese ? datiMese?.modalita_presenze : giornata.dati?.modalita_presenze) ||
          "banca_ore"
        }
        permessi={permessi}
        unita={preferenze.unita}
        fascia={fascia}
        passoAsse={fascia.passo}
        risorse={risorse}
        onChiudi={(): void => vai(pannelloMese ? { giorno: null } : { pannello: null })}
        onGiorno={(nuova): void =>
          vai(pannelloMese ? { giorno: nuova, mese: nuova.substr(0, 7) } : { data: nuova })
        }
        onMese={pannelloMese ? undefined : apriMese}
        onGiornata={pannelloMese ? apriGiornata : undefined}
        onNuovaPresenza={(persona, giorno): void => setNuovaPresenza({ persona, data: giorno })}
        onNuovaRichiesta={(persona, giorno): void => apriNuovaRichiesta(persona, giorno)}
      />

      {nuovaPresenza && (
        <CreatePresenceDrawer
          visible
          modalLoading={false}
          users={utenti}
          currentDate={moment(nuovaPresenza.data)}
          initialValues={valoriPresenza}
          onClose={(rileggere?: boolean): void => {
            setNuovaPresenza(null);
            if (rileggere) rileggi();
          }}
          updateAbsences={(): void => undefined}
        />
      )}

      {nuovaRichiesta && (
        <RequestCreateDrawer
          visible
          canGetAllUsers={false}
          helperUsersList={utenti || []}
          valoriIniziali={valoriRichiesta}
          onClose={(): void => setNuovaRichiesta(null)}
          onSave={salvaRichiesta}
        />
      )}
    </UITransition>
  );
};
